import { createContext, type FC, type PropsWithChildren, useContext, useMemo } from "react"
import { useQuery } from "urql"
import { graphql } from "@/graphql"
import { TenantMembershipRole } from "@/graphql/graphql"
import { useCurrentUser } from "./useCurrentUser"

const TenantAccessQuery = graphql(`
  query TenantAccess {
    tenant {
      currentMember {
        id
        role
        status
      }
    }
  }
`)

type TenantAccessValue = {
  canManageTenant: boolean
  isManager: boolean
  isResolving: boolean
  role: TenantMembershipRole | null
}

const TenantAccessContext = createContext<TenantAccessValue>({
  canManageTenant: false,
  isManager: false,
  isResolving: true,
  role: null,
})

export const TenantAccessProvider: FC<PropsWithChildren> = ({ children }) => {
  const currentUser = useCurrentUser()
  const [{ data, fetching }] = useQuery({
    query: TenantAccessQuery,
    pause: !currentUser,
  })

  const value = useMemo<TenantAccessValue>(() => {
    if (!currentUser) {
      return {
        canManageTenant: false,
        isManager: false,
        isResolving: false,
        role: null,
      }
    }

    const role = data?.tenant?.currentMember?.role ?? null
    const isManager = role === TenantMembershipRole.Manager

    return {
      canManageTenant: isManager,
      isManager,
      isResolving: fetching,
      role,
    }
  }, [currentUser, data?.tenant?.currentMember?.role, fetching])

  return <TenantAccessContext.Provider value={value}>{children}</TenantAccessContext.Provider>
}

export const useTenantAccess = () => useContext(TenantAccessContext)
